<template>
  <div class="bootstrap-data-table card datatable-card" data-tour="datatable">
    <div
      v-if="hasHeader"
      class="card-header datatable-card-header d-flex flex-wrap justify-content-between align-items-center gap-2"
    >
      <div class="datatable-action-zone d-flex flex-wrap align-items-center">
        <div
          v-if="showRefresh || showExport || showPrint"
          class="btn-group btn-group-sm datatable-actions"
          data-tour="datatable-actions"
          role="group"
          aria-label="Actions du tableau"
        >
          <button
            v-if="showRefresh"
            type="button"
            class="btn btn-outline-secondary"
            :disabled="loading"
            @click="refreshTable"
          >
            <i class="fa fa-rotate-right"></i>
            <span>Actualiser</span>
          </button>
          <button
            v-if="showExport"
            type="button"
            class="btn btn-outline-success"
            :disabled="loading || !items.length"
            @click="exportVisibleRows"
          >
            <i class="fa fa-file-excel"></i>
            <span>Excel</span>
          </button>
          <button
            v-if="showPrint"
            type="button"
            class="btn btn-outline-primary"
            :disabled="loading || !items.length"
            @click="printVisibleRows"
          >
            <i class="fa fa-print"></i>
            <span>Imprimer</span>
          </button>
        </div>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm btn-icon-label datatable-columns-button"
          title="Choisir les colonnes affichées"
          @click="openColumnsDialog"
        >
          <i class="fa fa-table-columns" aria-hidden="true"></i>
          <span class="btn-label">Colonnes</span>
        </button>
      </div>

      <div class="datatable-header-controls d-flex flex-wrap align-items-center justify-content-end gap-2">
        <div v-if="searchable" class="datatable-search" data-tour="datatable-search">
          <i class="fa fa-search datatable-search-icon"></i>
          <PrimevueInputText
            :modelValue="localSearchValue"
            type="text"
            class="w-100 datatable-search-input"
            :placeholder="searchPlaceholder"
            @update:modelValue="handleSearchInput"
            @keyup.enter="submitSearch"
          />
          <button
            v-if="localSearchValue"
            type="button"
            class="datatable-search-clear"
            :disabled="loading"
            title="Effacer la recherche"
            aria-label="Effacer la recherche"
            @click="clearSearch"
          >
            <i class="fa fa-xmark"></i>
          </button>
        </div>

        <slot name="header-extra"></slot>
      </div>
    </div>

    <div class="card-body p-0">
      <div
        v-if="selectionEnabled && selectedCount > 0"
        class="datatable-selection-toolbar d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2"
        data-test="datatable-selection-toolbar"
      >
        <div class="d-flex flex-wrap align-items-center gap-2">
          <strong class="datatable-selection-count" role="status">
            {{ selectedCount }} {{ selectedCount > 1 ? 'lignes sélectionnées' : 'ligne sélectionnée' }}
          </strong>
          <span
            v-if="isSelectionOverLimit"
            class="text-danger small fw-semibold"
            data-test="datatable-selection-limit-warning"
            role="alert"
          >
            La limite de {{ normalizedBulkActionLimit }} lignes par action est dépassée.
          </span>
        </div>

        <div class="d-flex flex-wrap align-items-center gap-2">
          <fieldset
            class="datatable-bulk-actions d-flex flex-wrap align-items-center gap-2 m-0 p-0 border-0"
            :disabled="selectionDisabled || isSelectionOverLimit"
          >
            <slot
              name="bulk-actions"
              :selected-ids="normalizedSelectedRowIds"
              :selected-count="selectedCount"
              :is-over-limit="isSelectionOverLimit"
              :clear-selection="clearSelection"
            ></slot>
          </fieldset>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            data-test="datatable-clear-selection"
            :disabled="selectionDisabled"
            @click="clearSelection"
          >
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Effacer la sélection
          </button>
        </div>
      </div>

      <div
        v-if="hasActiveStateBadge"
        class="datatable-active-state px-3 py-2"
        data-test="datatable-active-state-badge"
        role="status"
      >
        <span class="datatable-active-state-badge">
          <i class="fa fa-filter"></i>
          <span>{{ activeStateBadgeText }}</span>
        </span>
      </div>

      <div v-if="loading" class="text-center py-4">
        <i class="fa fa-spinner fa-spin me-1"></i>
        Chargement...
      </div>

      <div v-else>
        <div class="table-responsive datatable-responsive" data-tour="datatable-body">
          <table class="table table-striped table-hover align-middle mb-0 datatable-table">
            <thead class="table-light">
              <tr>
                <th
                  v-if="selectionEnabled"
                  scope="col"
                  class="datatable-selection-column d-print-none"
                >
                  <input
                    type="checkbox"
                    class="form-check-input m-0"
                    data-test="datatable-select-visible"
                    :checked="allVisibleRowsSelected"
                    :indeterminate="someVisibleRowsSelected"
                    :disabled="selectionDisabled || visibleSelectableIds.length === 0"
                    :aria-checked="someVisibleRowsSelected ? 'mixed' : String(allVisibleRowsSelected)"
                    :aria-label="allVisibleRowsSelected ? 'Désélectionner les lignes de la page' : 'Sélectionner les lignes de la page'"
                    @click.stop
                    @keydown.stop
                    @keyup.stop
                    @change.stop="handleVisibleSelectionChange"
                  />
                </th>
                <th
                  v-for="column in visibleColumns"
                  :key="column.key"
                  scope="col"
                  :class="['datatable-header-cell', 'text-nowrap', column.headerClass, { 'datatable-sortable': column.sortable }]"
                  :style="column.style || null"
                  @click="column.sortable && changeSort(column)"
                >
                  <span class="datatable-header-label d-inline-flex align-items-center gap-1 text-nowrap">
                    {{ column.label }}
                    <i
                      v-if="column.sortable"
                      class="fa"
                      :class="sortIcon(column.key)"
                    ></i>
                  </span>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="row in items"
                :key="row[rowKey]"
                class="datatable-row"
                :class="{ 'datatable-row-selected': selectionEnabled && isRowSelected(row) }"
                :aria-selected="selectionEnabled ? isRowSelected(row) : undefined"
                tabindex="0"
                @click="openRow(row)"
                @keyup.enter="openRow(row)"
              >
                <td
                  v-if="selectionEnabled"
                  class="datatable-selection-column d-print-none"
                  @click.stop
                  @keydown.stop
                  @keyup.stop
                >
                  <input
                    type="checkbox"
                    class="form-check-input m-0"
                    data-test="datatable-row-selection"
                    :checked="isRowSelected(row)"
                    :disabled="selectionDisabled || !canSelectRow(row)"
                    :aria-label="rowSelectionLabel(row)"
                    @click.stop
                    @keydown.stop
                    @keyup.stop
                    @change.stop="handleRowSelectionChange(row, $event)"
                  />
                </td>
                <td
                  v-for="column in visibleColumns"
                  :key="`${row[rowKey]}-${column.key}`"
                  :class="column.class"
                >
                  <slot
                    :name="`cell-${column.key}`"
                    :row="row"
                    :value="valueFor(row, column.key)"
                  >
                    {{ displayValue(valueFor(row, column.key)) }}
                  </slot>
                </td>
              </tr>

              <tr v-if="!items.length">
                <td :colspan="visibleColumns.length + (selectionEnabled ? 1 : 0)" class="text-center text-muted py-4">
                  {{ emptyText }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div
      v-if="!loading && showFooter"
      class="card-footer datatable-footer d-flex flex-wrap justify-content-between align-items-center gap-2"
    >
      <div class="d-flex flex-wrap align-items-center gap-2">
        <small class="text-muted">
          {{ footerRangeText }}
        </small>

        <select
          v-if="canChangePerPage"
          v-model.number="localPerPage"
          class="form-select form-select-sm datatable-per-page"
          data-tour="datatable-per-page"
          aria-label="Nombre de lignes à charger"
          @change="changeRowsPerPage"
        >
          <option v-for="option in perPageChoices" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>

      <div v-if="pagination" class="btn-group btn-group-sm datatable-pagination" role="group" aria-label="Pagination" data-tour="datatable-pagination">
        <button
          type="button"
          class="btn btn-outline-secondary datatable-page-button"
          :disabled="pagination.current_page <= 1"
          aria-label="Page précédente"
          @click="changePage(pagination.current_page - 1)"
        >
          <i class="fa fa-chevron-left"></i>
        </button>
        <template v-for="item in paginationItems" :key="item.key">
          <button
            v-if="item.type === 'page'"
            type="button"
            class="btn datatable-page-button"
            :class="item.active ? 'btn-primary active' : 'btn-outline-secondary'"
            :aria-current="item.active ? 'page' : undefined"
            :disabled="item.active"
            @click="changePage(item.page)"
          >
            {{ item.page }}
          </button>
          <button
            v-else
            type="button"
            class="btn btn-outline-secondary datatable-pagination-ellipsis"
            disabled
            aria-hidden="true"
          >
            …
          </button>
        </template>
        <button
          type="button"
          class="btn btn-outline-secondary datatable-page-button"
          :disabled="pagination.current_page >= pagination.last_page"
          aria-label="Page suivante"
          @click="changePage(pagination.current_page + 1)"
        >
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <PrimevueDialog
      v-model:visible="columnsDialogVisible"
      modal
      header="Colonnes affichées"
      :style="{ width: '460px', maxWidth: 'calc(100vw - 2rem)' }"
    >
      <p class="text-muted small mb-3">
        Sélectionnez les colonnes à afficher et définissez leur ordre dans le tableau, les exports et l’impression.
      </p>

      <p class="text-muted small" :id="columnCheckboxId('drag-help')">Glissez la poignée pour déplacer une colonne, ou utilisez les flèches du clavier.</p>
      <div
        ref="columnOptions"
        class="datatable-column-options"
        @pointermove="moveColumnDrag"
        @pointerup="finishColumnDrag"
        @pointercancel="cancelColumnDrag"
        @lostpointercapture="finishColumnDrag"
      >
        <div
          v-for="column in draftOrderedColumns"
          :key="column.key"
          class="datatable-column-option"
          :class="{ 'is-dragging': draggedColumnKey === column.key && columnDragMoved }"
          :data-column-key="column.key"
        >
          <button
            type="button"
            class="datatable-column-handle"
            :aria-label="`Déplacer ${column.label}`"
            :aria-describedby="columnCheckboxId('drag-help')"
            @pointerdown="startColumnDrag($event, column.key)"
            @keydown="onColumnOrderKeydown($event, column.key)"
            @click.prevent.stop
          ><i class="fa fa-grip-vertical" aria-hidden="true"></i></button>
          <PrimevueCheckbox
            v-model="draftVisibleColumnKeys"
            :inputId="columnCheckboxId(column.key)"
            :value="column.key"
          />
          <label class="flex-grow-1 mb-0" :for="columnCheckboxId(column.key)">{{ column.label }}</label>
        </div>
      </div>
      <p class="visually-hidden" aria-live="polite">{{ columnOrderAnnouncement }}</p>

      <div v-if="!draftVisibleColumnKeys.length" class="alert alert-warning py-2 mt-3 mb-0">
        Au moins une colonne doit rester affichée.
      </div>

      <template #footer>
        <div class="d-flex flex-wrap justify-content-between gap-2 w-100">
          <div class="d-flex flex-wrap gap-2">
            <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="selectAllColumns">
              <i class="fa fa-check-double" aria-hidden="true"></i>
              <span class="btn-label">Tout afficher</span>
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetColumnSelection">
              <i class="fa fa-rotate-left" aria-hidden="true"></i>
              <span class="btn-label">Réinitialiser</span>
            </button>
          </div>
          <div class="d-flex flex-wrap gap-2">
            <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="closeColumnsDialog">
              <i class="fa fa-xmark" aria-hidden="true"></i>
              <span class="btn-label">Annuler</span>
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm btn-icon-label"
              :disabled="!draftVisibleColumnKeys.length"
              @click="applyColumnSelection"
            >
              <i class="fa fa-check" aria-hidden="true"></i>
              <span class="btn-label">Appliquer</span>
            </button>
          </div>
        </div>
      </template>
    </PrimevueDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { exportArrayToExcel } from '@/utils/excelExport';
import {
  loadDataTableState,
  saveDataTableState,
} from '@/support/datatableState';

interface ColumnDefinition {
  key: string;
  label: string;
  hideable?: boolean;
  sortable?: boolean;
  class?: string;
  headerClass?: string;
  style?: string | Record<string, string>;
}

interface PaginationMeta {
  current_page: number;
  from: number | null;
  last_page: number;
  per_page: number;
  to: number | null;
  total: number;
}

type PaginationItem =
  | { type: 'page'; key: string; page: number; active: boolean }
  | { type: 'ellipsis'; key: string };

type SelectionId = string | number;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export default defineComponent({
  name: 'BootstrapDataTable',
  props: {
    response: {
      type: Object as PropType<Record<string, unknown> | null>,
      default: null,
    },
    rows: {
      type: Array as PropType<Record<string, unknown>[]>,
      default: () => [],
    },
    columns: {
      type: Array as PropType<ColumnDefinition[]>,
      required: true,
    },
    rowKey: {
      type: String,
      default: 'id',
    },
    detailRouteName: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'Aucune donnée disponible.',
    },
    sortBy: {
      type: String,
      default: '',
    },
    sortDir: {
      type: String as PropType<'asc' | 'desc' | ''>,
      default: '',
    },
    clientPagination: {
      type: Boolean,
      default: true,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    perPageOptions: {
      type: Array as PropType<number[]>,
      default: () => [5, 10, 25, 50, 100],
    },
    exportFileName: {
      type: String,
      default: 'liste-affichee.xlsx',
    },
    exportSheetName: {
      type: String,
      default: 'Liste',
    },
    printTitle: {
      type: String,
      default: 'Liste affichée',
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    searchValue: {
      type: String,
      default: '',
    },
    searchPlaceholder: {
      type: String,
      default: 'Rechercher...',
    },
    searchDebounce: {
      type: Number,
      default: 350,
    },
    showRefresh: {
      type: Boolean,
      default: true,
    },
    showExport: {
      type: Boolean,
      default: true,
    },
    showPrint: {
      type: Boolean,
      default: true,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    activeFilterCount: {
      type: Number,
      default: 0,
    },
    activeFilterLabels: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    showActiveStateBadge: {
      type: Boolean,
      default: true,
    },
    stateKey: {
      type: String,
      default: '',
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selectedRowIds: {
      type: Array as PropType<SelectionId[]>,
      default: () => [],
    },
    isRowSelectable: {
      type: Function as PropType<(row: Record<string, unknown>) => boolean>,
      default: () => true,
    },
    selectionDisabled: {
      type: Boolean,
      default: false,
    },
    bulkActionLimit: {
      type: Number,
      default: 500,
    },
  },
  data() {
    return {
      localPage: 1,
      localPerPage: this.perPage,
      localSearchValue: this.searchValue,
      searchTimer: null as number | null,
      initialSortBy: this.sortBy,
      initialSortDir: this.sortDir,
      preserveNextRowsPageReset: false,
      columnsDialogVisible: false,
      selectedVisibleColumnKeys: this.columns
        .filter((column) => column.key !== 'actions' && Boolean(column.label.trim()) && column.hideable !== false)
        .map((column) => column.key),
      draftVisibleColumnKeys: [] as string[],
      columnOrderKeys: this.columns
        .filter((column) => column.key !== 'actions' && Boolean(column.label.trim()) && column.hideable !== false)
        .map((column) => column.key),
      draftColumnOrderKeys: [] as string[],
      draggedColumnKey: null as string | null,
      columnDragPointerId: null as number | null,
      columnDragOriginY: 0,
      columnDragMoved: false,
      columnDragOriginalOrder: [] as string[],
      columnOrderAnnouncement: '',
      knownSelectableColumnKeys: this.columns
        .filter((column) => column.key !== 'actions' && Boolean(column.label.trim()) && column.hideable !== false)
        .map((column) => column.key),
    };
  },
  emits: [
    'row-click',
    'sort-change',
    'page-change',
    'per-page-change',
    'refresh',
    'search-change',
    'update:searchValue',
    'update:selectedRowIds',
    'state-restored',
  ],
  created() {
    this.restorePersistedTableState();
  },
  beforeUnmount() {
    this.clearSearchTimer();
  },
  watch: {
    columnsDialogVisible(visible: boolean) {
      if (!visible) {
        this.cancelColumnDrag();
        this.draftVisibleColumnKeys = [...this.selectedVisibleColumnKeys];
        this.draftColumnOrderKeys = [...this.columnOrderKeys];
      }
    },
    response() {
      this.handleApiPaginationChange();
    },
    rows() {
      if (this.preserveNextRowsPageReset) {
        this.preserveNextRowsPageReset = false;
        this.$nextTick(() => this.clampClientPaginationState());
        return;
      }

      this.localPage = 1;
      this.persistTableState(1, this.localPerPage);
    },
    perPage(value: number) {
      this.localPerPage = value;
      this.localPage = 1;
    },
    sortBy() {
      this.localPage = 1;
    },
    sortDir() {
      this.localPage = 1;
    },
    searchValue(value: string) {
      this.localSearchValue = value;
    },
    columns: {
      deep: true,
      handler() {
        this.reconcileColumnSelection();
      },
    },
  },
  computed: {
    selectionEnabled(): boolean {
      return this.selectable && Boolean(this.$slots['bulk-actions']);
    },

    hasHeader(): boolean {
      return this.showRefresh
        || this.showExport
        || this.showPrint
        || this.searchable
        || this.selectableColumns.length > 0
        || Boolean(this.$slots['header-extra']);
    },

    selectableColumns(): ColumnDefinition[] {
      return this.columns.filter((column) => (
        column.key !== 'actions'
        && Boolean(column.label.trim())
        && column.hideable !== false
      ));
    },

    visibleColumns(): ColumnDefinition[] {
      const selectedKeys = new Set(this.selectedVisibleColumnKeys);
      const selectableKeySet = new Set(this.selectableColumns.map((column) => column.key));
      const orderedColumns = this.orderedSelectableColumns.filter((column) => selectedKeys.has(column.key));
      let orderedIndex = 0;

      return this.columns.flatMap((column) => {
        if (!selectableKeySet.has(column.key)) return [column];
        const nextColumn = orderedColumns[orderedIndex];
        orderedIndex += 1;
        return nextColumn ? [nextColumn] : [];
      });
    },

    normalizedSelectedRowIds(): SelectionId[] {
      return this.normalizeSelectionIds(this.selectedRowIds);
    },

    selectedRowIdKeySet(): Set<string> {
      return new Set(this.normalizedSelectedRowIds.map((id) => this.selectionKey(id)));
    },

    selectedCount(): number {
      return this.normalizedSelectedRowIds.length;
    },

    normalizedBulkActionLimit(): number {
      const limit = Math.floor(Number(this.bulkActionLimit));
      return Number.isFinite(limit) && limit > 0 ? limit : 500;
    },

    isSelectionOverLimit(): boolean {
      return this.selectedCount > this.normalizedBulkActionLimit;
    },

    visibleSelectableIds(): SelectionId[] {
      return this.normalizeSelectionIds(
        this.items
          .filter((row) => this.canSelectRow(row))
          .map((row) => this.selectionIdFor(row)),
      );
    },

    allVisibleRowsSelected(): boolean {
      return this.visibleSelectableIds.length > 0
        && this.visibleSelectableIds.every((id) => this.selectedRowIdKeySet.has(this.selectionKey(id)));
    },

    someVisibleRowsSelected(): boolean {
      return !this.allVisibleRowsSelected
        && this.visibleSelectableIds.some((id) => this.selectedRowIdKeySet.has(this.selectionKey(id)));
    },

    orderedSelectableColumns(): ColumnDefinition[] {
      const columnsByKey = new Map(this.selectableColumns.map((column) => [column.key, column]));
      return this.normalizedColumnOrder(this.columnOrderKeys)
        .map((key) => columnsByKey.get(key))
        .filter(Boolean) as ColumnDefinition[];
    },

    draftOrderedColumns(): ColumnDefinition[] {
      const columnsByKey = new Map(this.selectableColumns.map((column) => [column.key, column]));

      return this.normalizedColumnOrder(this.draftColumnOrderKeys)
        .map((key) => columnsByKey.get(key))
        .filter(Boolean) as ColumnDefinition[];
    },

    sortedItems(): Record<string, unknown>[] {
      let items: Record<string, unknown>[];

      if (this.response && Array.isArray(this.response.data)) {
        items = this.response.data as Record<string, unknown>[];
      } else {
        items = this.rows;
      }

      if (!this.sortBy) return items;

      return [...items].sort((first, second) => {
        const firstValue = this.valueFor(first, this.sortBy);
        const secondValue = this.valueFor(second, this.sortBy);
        const direction = this.sortDir === 'desc' ? -1 : 1;

        if (firstValue == null && secondValue == null) return 0;
        if (firstValue == null) return -1 * direction;
        if (secondValue == null) return 1 * direction;

        if (typeof firstValue === 'number' && typeof secondValue === 'number') {
          return (firstValue - secondValue) * direction;
        }

        return String(firstValue).localeCompare(String(secondValue), 'fr') * direction;
      });
    },

    items(): Record<string, unknown>[] {
      if (!this.clientPagination || this.apiPagination) {
        return this.sortedItems;
      }

      const lastPage = Math.max(1, Math.ceil(this.sortedItems.length / this.localPerPage));
      const currentPage = Math.min(this.localPage, lastPage);
      const start = (currentPage - 1) * this.localPerPage;
      return this.sortedItems.slice(start, start + this.localPerPage);
    },

    apiPagination(): PaginationMeta | null {
      const meta = this.response?.meta;
      if (!isRecord(meta)) return null;

      const pagination = meta.pagination;
      if (!isRecord(pagination)) return null;

      return pagination as unknown as PaginationMeta;
    },

    clientPaginationMeta(): PaginationMeta | null {
      if (!this.clientPagination || this.apiPagination) return null;

      const total = this.sortedItems.length;
      if (total <= this.localPerPage && this.localPage <= 1) return null;

      const lastPage = Math.max(1, Math.ceil(total / this.localPerPage));
      const currentPage = Math.min(this.localPage, lastPage);
      const from = total === 0 ? null : (currentPage - 1) * this.localPerPage + 1;
      const to = total === 0 ? null : Math.min(currentPage * this.localPerPage, total);

      return {
        current_page: currentPage,
        from,
        last_page: lastPage,
        per_page: this.localPerPage,
        to,
        total,
      };
    },

    pagination(): PaginationMeta | null {
      return this.apiPagination || this.clientPaginationMeta;
    },

    paginationItems(): PaginationItem[] {
      if (!this.pagination) return [];

      const lastPage = Math.max(1, Number(this.pagination.last_page || 1));
      const currentPage = Math.min(
        Math.max(1, Number(this.pagination.current_page || 1)),
        lastPage,
      );

      if (lastPage <= 7) {
        return Array.from({ length: lastPage }, (_, index) => {
          const page = index + 1;

          return {
            type: 'page',
            key: `page-${page}`,
            page,
            active: page === currentPage,
          };
        });
      }

      const pages = new Set<number>([1, lastPage, currentPage, currentPage - 1, currentPage + 1]);

      if (currentPage <= 3) {
        pages.add(2);
        pages.add(3);
      }

      if (currentPage >= lastPage - 2) {
        pages.add(lastPage - 2);
        pages.add(lastPage - 1);
      }

      const sortedPages = Array.from(pages)
        .filter((page) => page >= 1 && page <= lastPage)
        .sort((first, second) => first - second);

      return sortedPages.reduce<PaginationItem[]>((items, page, index) => {
        const previousPage = sortedPages[index - 1];

        if (previousPage && page - previousPage > 1) {
          items.push({
            type: 'ellipsis',
            key: `ellipsis-${previousPage}-${page}`,
          });
        }

        items.push({
          type: 'page',
          key: `page-${page}`,
          page,
          active: page === currentPage,
        });

        return items;
      }, []);
    },

    canChangePerPage(): boolean {
      return this.clientPagination || Boolean(this.apiPagination);
    },

    perPageChoices(): number[] {
      const currentPerPage = this.apiPagination?.per_page || this.localPerPage;
      return Array.from(new Set([...this.perPageOptions, currentPerPage]))
        .filter((option) => Number.isFinite(option) && option > 0)
        .sort((first, second) => first - second);
    },

    footerRangeText(): string {
      if (this.pagination) {
        return `${this.pagination.from || 0}-${this.pagination.to || 0} sur ${this.pagination.total || 0}`;
      }

      const total = this.sortedItems.length;
      const label = total > 1 ? 'lignes affichées' : 'ligne affichée';

      return `${total} ${label}`;
    },

    exportColumns(): ColumnDefinition[] {
      return this.visibleColumns.filter((column) => {
        const label = column.label.trim();
        return Boolean(label) && column.key !== 'actions';
      });
    },

    hasActiveSearch(): boolean {
      return String(this.localSearchValue || '').trim().length > 0;
    },

    hasActiveSort(): boolean {
      return this.sortBy !== this.initialSortBy || this.sortDir !== this.initialSortDir;
    },

    normalizedActiveFilterCount(): number {
      return Math.max(0, Number(this.activeFilterCount || 0));
    },

    normalizedActiveFilterLabels(): string[] {
      return this.activeFilterLabels
        .map((label) => String(label || '').trim())
        .filter(Boolean);
    },

    activeFilterSummary(): string {
      const labels = this.normalizedActiveFilterLabels;
      const count = Math.max(this.normalizedActiveFilterCount, labels.length);

      if (count <= 0) return '';
      if (labels.length === 1 && count === 1) return labels[0];

      return `${count} ${count > 1 ? 'filtres' : 'filtre'}`;
    },

    activeStateParts(): string[] {
      const parts: string[] = [];

      if (this.hasActiveSearch) {
        parts.push('recherche active');
      }

      if (this.activeFilterSummary) {
        parts.push(this.activeFilterSummary);
      }

      if (this.hasActiveSort) {
        parts.push('tri actif');
      }

      return parts;
    },

    hasActiveStateBadge(): boolean {
      return this.showActiveStateBadge && this.activeStateParts.length > 0;
    },

    activeStateBadgeText(): string {
      return `Affichage filtré : ${this.activeStateParts.join(', ')}`;
    },
  },
  methods: {
    normalizeSelectionIds(ids: readonly unknown[]): SelectionId[] {
      const seen = new Set<string>();

      return ids.filter((id): id is SelectionId => {
        if (typeof id !== 'string' && typeof id !== 'number') return false;

        const key = this.selectionKey(id);
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
      });
    },

    selectionKey(id: SelectionId): string {
      return String(id);
    },

    selectionIdFor(row: Record<string, unknown>): SelectionId | null {
      const id = row[this.rowKey];
      return typeof id === 'string' || typeof id === 'number' ? id : null;
    },

    canSelectRow(row: Record<string, unknown>): boolean {
      return this.selectionIdFor(row) !== null && Boolean(this.isRowSelectable(row));
    },

    isRowSelected(row: Record<string, unknown>): boolean {
      const id = this.selectionIdFor(row);
      return id !== null && this.selectedRowIdKeySet.has(this.selectionKey(id));
    },

    rowSelectionLabel(row: Record<string, unknown>): string {
      const id = this.selectionIdFor(row);
      if (id === null || !this.canSelectRow(row)) return 'Ligne non sélectionnable';
      return `${this.isRowSelected(row) ? 'Désélectionner' : 'Sélectionner'} la ligne ${String(id)}`;
    },

    handleRowSelectionChange(row: Record<string, unknown>, event: Event): void {
      if (this.selectionDisabled || !this.canSelectRow(row)) return;

      const id = this.selectionIdFor(row);
      if (id === null) return;

      const checked = Boolean((event.target as HTMLInputElement | null)?.checked);
      const nextIds = [...this.normalizedSelectedRowIds];
      const selectedIdKeys = new Set(nextIds.map((selectedId) => this.selectionKey(selectedId)));
      const idKey = this.selectionKey(id);

      if (checked && !selectedIdKeys.has(idKey)) {
        nextIds.push(id);
      } else if (!checked && selectedIdKeys.has(idKey)) {
        this.$emit(
          'update:selectedRowIds',
          nextIds.filter((selectedId) => this.selectionKey(selectedId) !== idKey),
        );
        return;
      }

      this.$emit('update:selectedRowIds', nextIds);
    },

    handleVisibleSelectionChange(event: Event): void {
      if (this.selectionDisabled) return;
      const checked = Boolean((event.target as HTMLInputElement | null)?.checked);
      const visibleIdKeys = new Set(this.visibleSelectableIds.map((id) => this.selectionKey(id)));

      if (checked) {
        const nextIds = [...this.normalizedSelectedRowIds];
        const selectedIdKeys = new Set(nextIds.map((id) => this.selectionKey(id)));

        this.visibleSelectableIds.forEach((id) => {
          const key = this.selectionKey(id);
          if (!selectedIdKeys.has(key)) {
            nextIds.push(id);
            selectedIdKeys.add(key);
          }
        });

        this.$emit('update:selectedRowIds', nextIds);
        return;
      }

      this.$emit(
        'update:selectedRowIds',
        this.normalizedSelectedRowIds.filter((id) => !visibleIdKeys.has(this.selectionKey(id))),
      );
    },

    clearSelection(): void {
      this.$emit('update:selectedRowIds', []);
    },

    columnCheckboxId(key: string): string {
      return `datatable-${this.$.uid}-column-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    },

    openColumnsDialog(): void {
      this.draftVisibleColumnKeys = [...this.selectedVisibleColumnKeys];
      this.draftColumnOrderKeys = [...this.columnOrderKeys];
      this.columnsDialogVisible = true;
    },

    closeColumnsDialog(): void {
      this.cancelColumnDrag();
      this.draftVisibleColumnKeys = [...this.selectedVisibleColumnKeys];
      this.draftColumnOrderKeys = [...this.columnOrderKeys];
      this.columnsDialogVisible = false;
    },

    selectAllColumns(): void {
      this.draftVisibleColumnKeys = this.selectableColumns.map((column) => column.key);
    },

    resetColumnSelection(): void {
      this.selectAllColumns();
      this.draftColumnOrderKeys = this.selectableColumns.map((column) => column.key);
    },

    normalizedColumnOrder(keys: string[]): string[] {
      const selectableKeys = this.selectableColumns.map((column) => column.key);
      const selectableKeySet = new Set(selectableKeys);
      const retainedKeys = keys.filter((key) => selectableKeySet.has(key));
      return Array.from(new Set([...retainedKeys, ...selectableKeys]));
    },

    moveDraftColumn(key: string, direction: number): void {
      const order = this.normalizedColumnOrder(this.draftColumnOrderKeys);
      const index = order.indexOf(key);
      const targetIndex = index + direction;
      if (index < 0 || targetIndex < 0 || targetIndex >= order.length) return;

      order.splice(index, 1);
      order.splice(targetIndex, 0, key);
      this.draftColumnOrderKeys = order;
      const column = this.selectableColumns.find((entry) => entry.key === key);
      this.columnOrderAnnouncement = `${column?.label || key}, position ${targetIndex + 1} sur ${order.length}.`;
    },

    onColumnOrderKeydown(event: KeyboardEvent, key: string): void {
      if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const order = this.normalizedColumnOrder(this.draftColumnOrderKeys);
      const index = order.indexOf(key);
      const direction = event.key === 'Home' ? -index
        : event.key === 'End' ? order.length - 1 - index
          : event.key === 'ArrowUp' ? -1 : 1;
      this.moveDraftColumn(key, direction);
    },

    startColumnDrag(event: PointerEvent, key: string): void {
      if (event.button !== 0 || event.isPrimary === false || this.columnDragPointerId !== null) return;
      event.preventDefault();
      this.draggedColumnKey = key;
      this.columnDragPointerId = event.pointerId;
      this.columnDragOriginY = event.clientY;
      this.columnDragMoved = false;
      this.columnDragOriginalOrder = [...this.draftColumnOrderKeys];
      const handle = event.currentTarget as HTMLElement;
      handle.focus();
      // La liste reste en place quand Vue déplace les cartes : elle conserve la capture au toucher.
      const container = this.$refs.columnOptions as HTMLElement;
      container.setPointerCapture?.(event.pointerId);
    },

    moveColumnDrag(event: PointerEvent): void {
      if (event.pointerId !== this.columnDragPointerId || !this.draggedColumnKey) return;
      if (!this.columnDragMoved && Math.abs(event.clientY - this.columnDragOriginY) < 5) return;
      this.columnDragMoved = true;
      const container = this.$refs.columnOptions as HTMLElement | undefined;
      if (!container) return;
      const bounds = container.getBoundingClientRect();
      if (event.clientY < bounds.top + 28) container.scrollTop -= 12;
      if (event.clientY > bounds.bottom - 28) container.scrollTop += 12;
      const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-column-key]');
      if (!target || !container.contains(target)) return;
      const key = target.dataset.columnKey;
      if (!key || key === this.draggedColumnKey) return;
      const order = this.normalizedColumnOrder(this.draftColumnOrderKeys);
      const from = order.indexOf(this.draggedColumnKey);
      const to = order.indexOf(key);
      if (from < 0 || to < 0) return;
      // Attendre le passage du milieu de la carte évite les oscillations après réordonnancement.
      const targetBounds = target.getBoundingClientRect();
      const middle = targetBounds.top + targetBounds.height / 2;
      if ((from < to && event.clientY < middle) || (from > to && event.clientY > middle)) return;
      this.moveDraftColumn(this.draggedColumnKey, to - from);
    },

    finishColumnDrag(event?: PointerEvent): void {
      if (event && event.pointerId !== this.columnDragPointerId) return;
      this.draggedColumnKey = null;
      this.columnDragPointerId = null;
      this.columnDragMoved = false;
      this.columnDragOriginalOrder = [];
    },

    cancelColumnDrag(event?: PointerEvent): void {
      if (event && event.pointerId !== this.columnDragPointerId) return;
      if (this.draggedColumnKey) this.draftColumnOrderKeys = [...this.columnDragOriginalOrder];
      this.finishColumnDrag();
    },

    applyColumnSelection(): void {
      const selectableKeys = new Set(this.selectableColumns.map((column) => column.key));
      const selectedKeys = this.draftVisibleColumnKeys.filter((key) => selectableKeys.has(key));

      if (!selectedKeys.length) return;

      this.selectedVisibleColumnKeys = selectedKeys;
      this.columnOrderKeys = this.normalizedColumnOrder(this.draftColumnOrderKeys);
      this.knownSelectableColumnKeys = Array.from(selectableKeys);
      this.columnsDialogVisible = false;
      this.persistTableState(this.localPage, this.localPerPage);
    },

    reconcileColumnSelection(): void {
      const selectableKeys = this.selectableColumns.map((column) => column.key);
      const selectableKeySet = new Set(selectableKeys);
      const knownKeySet = new Set(this.knownSelectableColumnKeys);
      const retainedKeys = this.selectedVisibleColumnKeys.filter((key) => selectableKeySet.has(key));
      const newKeys = selectableKeys.filter((key) => !knownKeySet.has(key));
      const nextKeys = Array.from(new Set([...retainedKeys, ...newKeys]));

      this.selectedVisibleColumnKeys = nextKeys.length ? nextKeys : selectableKeys.slice(0, 1);
      this.columnOrderKeys = this.normalizedColumnOrder(this.columnOrderKeys);
      this.knownSelectableColumnKeys = selectableKeys;
      this.persistTableState(this.localPage, this.localPerPage);
    },

    valueFor(row: Record<string, unknown>, key: string): unknown {
      return key.split('.').reduce<unknown>((value, segment) => {
        if (!isRecord(value)) return undefined;
        return value[segment];
      }, row);
    },

    displayValue(value: unknown): string {
      if (value === null || value === undefined || value === '') return '-';
      return String(value);
    },

    sortIcon(key: string): string {
      if (this.sortBy !== key) return 'fa-sort text-muted';
      return this.sortDir === 'desc' ? 'fa-sort-down' : 'fa-sort-up';
    },

    changeSort(column: ColumnDefinition): void {
      const nextDir = this.sortBy === column.key && this.sortDir === 'asc' ? 'desc' : 'asc';
      this.$emit('sort-change', {
        sortBy: column.key,
        sortDir: nextDir,
      });
    },

    changePage(page: number): void {
      const targetPage = Number(page);
      const pagination = this.pagination;

      if (!Number.isFinite(targetPage) || targetPage < 1) {
        return;
      }

      if (pagination) {
        const currentPage = Number(pagination.current_page || 1);
        const lastPage = Number(pagination.last_page || 1);

        if (targetPage === currentPage || targetPage > lastPage) {
          return;
        }
      }

      if (this.clientPagination && !this.apiPagination) {
        this.localPage = targetPage;
        this.persistTableState(targetPage, this.localPerPage);
        return;
      }

      this.$emit('page-change', targetPage);
    },

    changeRowsPerPage(): void {
      this.localPage = 1;

      if (this.clientPagination && !this.apiPagination) {
        this.persistTableState(1, this.localPerPage);
        return;
      }

      this.$emit('per-page-change', this.localPerPage);
    },

    refreshTable(): void {
      this.$emit('refresh');
    },

    handleSearchInput(value: string): void {
      this.localSearchValue = value;
      this.$emit('update:searchValue', value);
      this.queueSearch(value);
    },

    queueSearch(value: string): void {
      this.clearSearchTimer();
      this.searchTimer = window.setTimeout(() => {
        this.$emit('search-change', value);
      }, this.searchDebounce);
    },

    submitSearch(): void {
      this.clearSearchTimer();
      this.$emit('update:searchValue', this.localSearchValue);
      this.$emit('search-change', this.localSearchValue);
    },

    clearSearch(): void {
      this.clearSearchTimer();
      this.localSearchValue = '';
      this.$emit('update:searchValue', '');
      this.$emit('search-change', '');
    },

    clearSearchTimer(): void {
      if (this.searchTimer !== null) {
        window.clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
    },

    restorePersistedTableState(): void {
      if (!this.stateKey) {
        this.reconcileColumnSelection();
        return;
      }

      const state = loadDataTableState(this.stateKey, this.$store);
      if (!state) return;

      this.localPage = state.page;
      this.localPerPage = state.perPage;
      const selectableKeys = this.selectableColumns.map((column) => column.key);
      const selectableKeySet = new Set(selectableKeys);

      if (state.visibleColumnKeys) {
        const knownKeys = new Set(state.columnKeys || state.visibleColumnKeys);
        const restoredKeys = state.visibleColumnKeys.filter((key) => selectableKeySet.has(key));
        const newKeys = selectableKeys.filter((key) => !knownKeys.has(key));
        const nextKeys = Array.from(new Set([...restoredKeys, ...newKeys]));

        this.selectedVisibleColumnKeys = nextKeys.length ? nextKeys : selectableKeys.slice(0, 1);
      } else {
        this.selectedVisibleColumnKeys = selectableKeys;
      }

      this.columnOrderKeys = this.normalizedColumnOrder(state.columnOrderKeys || selectableKeys);
      this.knownSelectableColumnKeys = selectableKeys;
      this.preserveNextRowsPageReset = true;
      this.$emit('state-restored', {
        page: state.page,
        perPage: state.perPage,
      });
    },

    handleApiPaginationChange(): void {
      const pagination = this.apiPagination;
      if (!pagination) return;

      const currentPage = Number(pagination.current_page || 1);
      const lastPage = Math.max(1, Number(pagination.last_page || 1));
      const perPage = Number(pagination.per_page || this.localPerPage || this.perPage);

      if (Number.isFinite(perPage) && perPage > 0) {
        this.localPerPage = perPage;
      }

      if (Number.isFinite(currentPage) && currentPage > lastPage) {
        this.$emit('page-change', lastPage);
        return;
      }

      this.persistTableState(currentPage, perPage);
    },

    persistTableState(page: number, perPage: number): void {
      if (!this.stateKey) return;

      saveDataTableState(this.stateKey, {
        page,
        perPage,
        visibleColumnKeys: this.selectedVisibleColumnKeys,
        columnKeys: this.knownSelectableColumnKeys,
        columnOrderKeys: this.columnOrderKeys,
      }, this.$store);
    },

    clampClientPaginationState(): void {
      if (!this.clientPagination || this.apiPagination) return;

      const lastPage = Math.max(1, Math.ceil(this.sortedItems.length / this.localPerPage));
      const nextPage = Math.min(this.localPage, lastPage);

      if (nextPage !== this.localPage) {
        this.localPage = nextPage;
      }

      this.persistTableState(this.localPage, this.localPerPage);
    },

    exportVisibleRows(): void {
      const rows = this.items.map((row) => this.serializeRow(row));

      exportArrayToExcel(rows, {
        fileName: this.exportFileName,
        sheetName: this.exportSheetName,
      });
    },

    // document.write ne bénéficie pas de l'échappement des interpolations Vue :
    // titres, en-têtes et cellules doivent tous passer par escapeHtml.
    printVisibleRows(): void {
      const columns = this.exportColumns;
      const headerHtml = columns
        .map((column) => `<th>${this.escapeHtml(column.label)}</th>`)
        .join('');
      const bodyHtml = this.items
        .map((row) => {
          const cells = columns
            .map((column) => `<td>${this.escapeHtml(this.displayValue(this.valueFor(row, column.key)))}</td>`)
            .join('');

          return `<tr>${cells}</tr>`;
        })
        .join('');
      const printWindow = window.open('', '_blank', 'width=1120,height=800');

      if (!printWindow) {
        window.print();
        return;
      }

      printWindow.document.write(`
        <!doctype html>
        <html lang="fr">
          <head>
            <meta charset="utf-8">
            <title>${this.escapeHtml(this.printTitle)}</title>
            <style>
              body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
              h1 { font-size: 18px; margin: 0 0 16px; }
              table { border-collapse: collapse; width: 100%; font-size: 12px; }
              th, td { border-bottom: 1px solid #d1d5db; padding: 8px 10px; text-align: left; }
              th { background: #f3f4f6; font-weight: 700; }
              .powered-by { margin-top: 18px; text-align: center; font-size: 9px; color: #6b7280; }
            </style>
          </head>
          <body>
            <h1>${this.escapeHtml(this.printTitle)}</h1>
            <table>
              <thead><tr>${headerHtml}</tr></thead>
              <tbody>${bodyHtml}</tbody>
            </table>
            <div class="powered-by">powered by Suite241</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    },

    serializeRow(row: Record<string, unknown>): Record<string, string> {
      return this.exportColumns.reduce<Record<string, string>>((payload, column) => {
        payload[column.label] = this.displayValue(this.valueFor(row, column.key));
        return payload;
      }, {});
    },

    escapeHtml(value: unknown): string {
      const entities: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };

      return String(value ?? '').replace(/[&<>"']/g, (character) => entities[character] || character);
    },

    openRow(row: Record<string, unknown>): void {
      this.$emit('row-click', row);

      if (this.detailRouteName && row[this.rowKey]) {
        this.$router.push({
          name: this.detailRouteName,
          params: { id: row[this.rowKey] as string | number },
        });
      }
    },
  },
});
</script>

<style scoped>
.datatable-card {
  border: 1px solid rgba(33, 37, 41, 0.08);
  border-radius: 8px;
  box-shadow: 0 0.35rem 1rem rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.datatable-card-header,
.datatable-footer {
  background: #fff;
  border-color: rgba(33, 37, 41, 0.08);
}

.datatable-actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
}

.datatable-columns-button {
  margin-left: 0.65rem;
}

.datatable-column-options {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr);
  max-height: min(52vh, 420px);
  overflow-y: auto;
  padding: 0.1rem;
}

.datatable-column-option {
  align-items: center;
  border: 1px solid rgba(33, 37, 41, 0.1);
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  gap: 0.65rem;
  min-height: 2.75rem;
  padding: 0.6rem 0.75rem;
}

.datatable-column-option:hover {
  background: rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.06);
  border-color: rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.28);
}

.datatable-column-option.is-dragging {
  background: rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.12);
  border-color: var(--bs-primary, #0d6efd);
}

.datatable-column-handle {
  border: 0;
  border-radius: 0.3rem;
  background: transparent;
  color: #667085;
  cursor: grab;
  padding: 0.4rem 0.6rem;
  touch-action: none;
  user-select: none;
}

.datatable-column-handle:active {
  cursor: grabbing;
}

.datatable-column-handle:focus-visible {
  outline: 2px solid var(--bs-primary, #0d6efd);
}

.datatable-header-controls {
  flex: 1 1 280px;
}

.datatable-search {
  flex: 0 1 320px;
  max-width: 100%;
  min-width: min(100%, 260px);
  position: relative;
}

.datatable-search-icon {
  color: #6c757d;
  left: 0.75rem;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
}

.datatable-search :deep(.datatable-search-input) {
  height: 2rem;
  padding-left: 2.15rem;
  padding-right: 2rem;
}

.datatable-search-clear {
  align-items: center;
  background: transparent;
  border: 0;
  color: #6c757d;
  display: inline-flex;
  height: 1.65rem;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.65rem;
  z-index: 3;
}

.datatable-search-clear:hover,
.datatable-search-clear:focus {
  color: #212529;
}

.datatable-active-state {
  background: #fff5f5;
  border-bottom: 1px solid rgba(220, 53, 69, 0.18);
}

.datatable-selection-toolbar {
  background: rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.06);
  border-bottom: 1px solid rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.18);
}

.datatable-selection-count {
  color: var(--suite241-admin-primary, #0d6efd);
  font-size: 0.88rem;
}

.datatable-bulk-actions:disabled {
  opacity: 0.65;
}

.datatable-active-state-badge {
  align-items: center;
  background: #dc3545;
  border-radius: 999px;
  box-shadow: 0 0.35rem 0.85rem rgba(220, 53, 69, 0.18);
  color: #fff;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 0.4rem;
  line-height: 1.2;
  padding: 0.35rem 0.65rem;
}

.datatable-responsive {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.datatable-table {
  min-width: 760px;
}

.datatable-table > :not(caption) > * > * {
  border-left: 0;
  border-right: 0;
}

.datatable-header-cell,
.datatable-header-label {
  white-space: nowrap;
}

.datatable-selection-column {
  text-align: center;
  width: 3rem;
}

.datatable-selection-column .form-check-input {
  cursor: pointer;
  float: none;
}

.datatable-selection-column .form-check-input:disabled {
  cursor: not-allowed;
}

.datatable-row-selected > td {
  background-color: rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.08);
}

.datatable-footer {
  padding: 0.75rem 1rem;
}

.datatable-per-page {
  width: 82px;
}

.datatable-sortable {
  cursor: pointer;
  user-select: none;
}

.datatable-row {
  cursor: pointer;
}

.datatable-row:focus {
  outline: 2px solid rgba(13, 110, 253, 0.35);
  outline-offset: -2px;
}

@media (max-width: 575.98px) {
  .datatable-actions,
  .datatable-action-zone,
  .datatable-header-controls,
  .datatable-search {
    flex: 1 1 100%;
    width: 100%;
  }

  .datatable-actions {
    display: flex;
  }

  .datatable-columns-button {
    margin-left: 0;
    margin-top: 0.5rem;
  }
}
</style>
